import { useState, useEffect } from "react";
import { T } from "./theme";
import { Building2, TrendingUp, TrendingDown, AlertTriangle, Network, FileBarChart, X, Copy, Check, ChevronRight, Download, ArrowLeft, BarChart3, Search } from "lucide-react";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";
import Input from "./components/Input";

function KpiBox({ label, value, sub, color, icon: Icon }) {
  return (
    <Card style={{ flex: 1, minWidth: 160, display: "flex", flexDirection: "column", padding: "20px 24px", position: "relative", overflow: "hidden" }}>
      {/* Subtle indicator gradient */}
      <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "30%", background: `linear-gradient(90deg, transparent, ${color}08)`, pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: T.textMid, fontWeight: 600, fontFamily: T.fontBody }}>{label}</span>
        <div style={{ width: T.iconBoxSizeSm, height: T.iconBoxSizeSm, borderRadius: T.iconBoxRadiusSm, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={16} color={color} strokeWidth={2.5}/>
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, fontFamily: T.fontTitle, color, lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.textLow, marginTop: 8, fontWeight: 500, fontFamily: T.fontBody }}>{sub}</div>}
    </Card>
  );
}

function getNPSColor(nps) {
  if (nps >= 75)  return T.green;
  if (nps >= 50)  return T.accent;
  if (nps >= 0)   return T.amber;
  return T.red;
}

function getNPSBadge(nps) {
  if (nps >= 75)  return { label: "Excelente", color: T.green  };
  if (nps >= 50)  return { label: "Muito Bom", color: T.accent };
  if (nps >= 25)  return { label: "Bom",       color: T.teal   };
  if (nps >= 0)   return { label: "Neutro",    color: T.amber  };
  return           { label: "Crítico",          color: T.red    };
}

const PRINT_STYLE = `
@media print {
  .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
  * { box-shadow: none !important; }
}
`;

/* --- CSV Export ----------------------------------------------------------- */
function exportCSV(hospitais) {
  if (!hospitais || hospitais.length === 0) return;
  const allAreas = new Set();
  hospitais.forEach(h => Object.keys(h.areas || {}).forEach(a => allAreas.add(a)));
  const areaList = [...allAreas].sort();

  const header = ["Hospital", "NPS Médio", "Respostas", ...areaList.map(a => `NPS ${a}`)];
  const rows = hospitais.map(h => [
    h.nome,
    h.nps,
    h.respostas || 0,
    ...areaList.map(a => h.areas?.[a]?.nota ?? ""),
  ]);

  const csv = [header, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `crm-rede-${new Date().toISOString().slice(0,10)}.csv` });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* --- Area Bar Chart ------------------------------------------------------ */
function AreaBarChart({ areas }) {
  const entries = Object.entries(areas || {}).sort((a, b) => b[1].nota - a[1].nota);
  if (entries.length === 0) return <div style={{ color: T.textLow, fontSize: 13, fontFamily: T.fontBody }}>Sem dados de áreas.</div>;

  const maxNPS = Math.max(...entries.map(([, d]) => Math.abs(d.nota)), 100);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {entries.map(([area, d]) => {
        const color = getNPSColor(d.nota);
        const pct = Math.max(4, (Math.abs(d.nota) / maxNPS) * 100);
        return (
          <div key={area}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: T.textMid, fontWeight: 500, fontFamily: T.fontBody }}>{area}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: T.textLow, fontWeight: 500, fontFamily: T.fontBody }}>{d.respostas || 0} resp.</span>
                <span style={{ fontSize: 14, fontWeight: 700, fontFamily: T.fontTitle, color }}>
                  {d.nota >= 0 ? "+" : ""}{d.nota}
                </span>
              </div>
            </div>
            <div style={{ height: 10, borderRadius: 5, background: T.bgBase, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 5, background: color,
                width: `${pct}%`, transition: T.transitionSlow,
              }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* --- Hospital Detail Panel ----------------------------------------------- */
function HospitalDetail({ hospital, onClose, onGenerateReport }) {
  const [gerandoRel, setGerandoRel] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!hospital) return null;

  const color = getNPSColor(hospital.nps);
  const badge = getNPSBadge(hospital.nps);
  const areas = hospital.areas || {};
  const areaEntries = Object.entries(areas).sort((a, b) => b[1].nota - a[1].nota);
  const bestArea = areaEntries[0];
  const worstArea = areaEntries[areaEntries.length - 1];

  const handleGenerateReport = async () => {
    setGerandoRel(true);
    try {
      const r = await fetch("/api/crm/relatorio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ unidade: hospital.nome }),
      });
      const d = await r.json();
      if (d.relatorio) setRelatorio(d.relatorio);
    } catch {}
    setGerandoRel(false);
  };

  const copyReport = () => {
    if (!relatorio) return;
    const txt = [
      `Relatório: ${hospital.nome}`,
      relatorio.resumo || "",
      "\nPontos Fortes:\n" + (relatorio.pontos_fortes || []).map(p => "- " + p).join("\n"),
      "\nPontos de Melhoria:\n" + (relatorio.pontos_melhoria || []).map(p => "- " + p).join("\n"),
      "\nRecomendações:\n" + (relatorio.recomendacoes_prioritarias || []).map(r => `- ${r.acao} (${r.prazo})`).join("\n"),
      relatorio.proximo_ciclo ? "\nPróximo Ciclo:\n" + relatorio.proximo_ciclo : "",
    ].join("\n");
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <PageContainer maxWidth={1200}>
      {/* Back button */}
      <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onClose} className="no-print" style={{ marginBottom: 24 }}>
        Voltar para a Rede
      </Button>

      {/* Hospital Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: T.radius,
            background: `${color}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Building2 size={26} color={color}/>
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontFamily: T.fontTitle, fontWeight: T.titleWeight, color: T.textHi, letterSpacing: "-0.02em" }}>
              {hospital.nome}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
              <Badge color={badge.color} size="md">
                {badge.label}
              </Badge>
              <span style={{ fontSize: 13, color: T.textLow, fontWeight: 500, fontFamily: T.fontBody }}>
                {hospital.respostas ? hospital.respostas.toLocaleString("pt-BR") : 0} respostas registradas
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }} className="no-print">
          <Button variant="secondary" icon={FileBarChart} loading={gerandoRel} onClick={handleGenerateReport}
            style={{ border: `1px solid ${T.accent}`, color: T.accent, boxShadow: "0 4px 12px rgba(99,102,241,0.15)" }}>
            {gerandoRel ? "Gerando Relatório..." : "Relatório Executivo"}
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <KpiBox label="NPS Geral" value={`${hospital.nps >= 0 ? "+" : ""}${hospital.nps}`} icon={BarChart3} color={color}/>
        <KpiBox label="Áreas Avaliadas" value={areaEntries.length} icon={Network} color={T.accent}/>
        {bestArea && (
          <KpiBox label="Melhor Área"
            value={`NPS ${bestArea[1].nota >= 0 ? "+" : ""}${bestArea[1].nota}`}
            sub={bestArea[0]}
            icon={TrendingUp}
            color={T.green}/>
        )}
        {worstArea && areaEntries.length > 1 && (
          <KpiBox label="Pior Área"
            value={`NPS ${worstArea[1].nota >= 0 ? "+" : ""}${worstArea[1].nota}`}
            sub={worstArea[0]}
            icon={TrendingDown}
            color={worstArea[1].nota < 0 ? T.red : T.amber}/>
        )}
      </div>

      {/* Content Layout */}
      <div style={{ display: "flex", gap: 24, flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Left Column: Breakdown */}
        <div style={{ flex: 1, minWidth: 320, display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Area Breakdown */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ width: T.iconBoxSizeSm, height: T.iconBoxSizeSm, borderRadius: T.iconBoxRadiusSm, background: T.accentDim, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BarChart3 size={16} color={T.accent}/>
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>NPS por Área</span>
            </div>
            <AreaBarChart areas={areas}/>
          </Card>
        </div>

        {/* Right Column: Table */}
        <div style={{ flex: 2, minWidth: 500, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Area Detail Table */}
            <Card noPadding>
              <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, background: T.bgBase }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>Detalhamento Completo das Áreas</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {["Área", "NPS", "Ranking", "Respostas", "Status"].map(h => (
                      <th key={h} style={{ padding: T.tableHeaderPadding, textAlign: "left", fontSize: T.tableHeaderFontSize, color: T.textLow, fontWeight: T.tableHeaderWeight, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.fontBody }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {areaEntries.map(([area, d]) => {
                    const aColor = getNPSColor(d.nota);
                    const aBadge = getNPSBadge(d.nota);
                    return (
                      <tr key={area}
                        style={{ borderBottom: `1px solid ${T.border}`, transition: T.transitionFast, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: T.tableCellPadding, fontSize: 14, color: T.textHi, fontWeight: 600, fontFamily: T.fontBody }}>{area}</td>
                        <td style={{ padding: T.tableCellPadding }}>
                          <span style={{ fontSize: 18, fontWeight: 800, fontFamily: T.fontTitle, color: aColor }}>
                            {d.nota >= 0 ? "+" : ""}{d.nota}
                          </span>
                        </td>
                        <td style={{ padding: T.tableCellPadding, fontSize: 13, color: T.textMid, fontWeight: 500, fontFamily: T.fontBody }}>
                          {d.posicao ? `${d.posicao}º lugar` : "—"}
                        </td>
                        <td style={{ padding: T.tableCellPadding, fontSize: 13, color: T.textMid, fontWeight: 500, fontFamily: T.fontBody }}>
                          {d.respostas ? d.respostas.toLocaleString("pt-BR") : "—"}
                        </td>
                        <td style={{ padding: T.tableCellPadding }}>
                          <Badge color={aBadge.color} size="md">
                            {aBadge.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                  {areaEntries.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: T.textLow, fontSize: 14, fontWeight: 500, fontFamily: T.fontBody }}>
                      Sem dados de áreas disponíveis para esta unidade.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </Card>
        </div>
      </div>

      {/* Report section (inline) */}
      {relatorio && (
        <Card style={{ marginTop: 24, background: T.gradientAccent, border: "none", color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: T.iconBoxSize, height: T.iconBoxSize, borderRadius: T.iconBoxRadius, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileBarChart size={20} color="#fff"/>
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: T.fontTitle }}>Relatório Executivo HFocus</span>
            </div>
            <div style={{ display: "flex", gap: 12 }} className="no-print">
              <button onClick={copyReport} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: T.radiusSm,
                background: "rgba(255,255,255,0.15)", border: "none",
                color: "#fff", fontSize: 13, cursor: "pointer", fontFamily: T.fontBody, fontWeight: 600,
                transition: T.transitionFast,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
              >
                {copied ? <Check size={14}/> : <Copy size={14}/>}
                {copied ? "Texto Copiado!" : "Copiar Texto"}
              </button>
            </div>
          </div>

          <div style={{ background: T.bgCard, borderRadius: T.radius, padding: 32, color: T.textHi }}>
            {relatorio.resumo && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, fontFamily: T.fontBody }}>Resumo de Performance</div>
                <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, margin: 0, fontFamily: T.fontBody }}>{relatorio.resumo}</p>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
              {(relatorio.pontos_fortes || []).length > 0 && (
                <div style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: T.radius, padding: "20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: T.green, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontFamily: T.fontBody }}>Principais Fortalezas</div>
                  {relatorio.pontos_fortes.map((p, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 13, color: T.textMid, lineHeight: 1.6, fontWeight: 500, fontFamily: T.fontBody }}>
                      <span style={{ color: T.green, flexShrink: 0, fontWeight: 800 }}>+</span> {p}
                    </div>
                  ))}
                </div>
              )}
              {(relatorio.pontos_melhoria || []).length > 0 && (
                <div style={{ background: "rgba(244,63,94,0.05)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: T.radius, padding: "20px" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: T.red, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontFamily: T.fontBody }}>Oportunidades de Melhoria</div>
                  {relatorio.pontos_melhoria.map((p, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 13, color: T.textMid, lineHeight: 1.6, fontWeight: 500, fontFamily: T.fontBody }}>
                      <span style={{ color: T.red, flexShrink: 0, fontWeight: 800 }}>!</span> {p}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(relatorio.recomendacoes_prioritarias || []).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontFamily: T.fontBody }}>Ação Recomendada (PDSA IA)</div>
                {relatorio.recomendacoes_prioritarias.map((r, i) => (
                  <div key={i} style={{ padding: T.tableCellPadding, borderRadius: 14, background: T.bgBase, border: `1px solid ${T.border}`, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <span style={{ fontSize: 14, color: T.textHi, fontWeight: 600, fontFamily: T.fontBody }}>{r.acao}</span>
                      {r.prazo && <Badge color={T.accent}>{r.prazo}</Badge>}
                    </div>
                    {r.impacto && <div style={{ fontSize: 13, color: T.textMid, marginTop: 6, fontWeight: 500, fontFamily: T.fontBody }}><strong>Impacto Esperado:</strong> {r.impacto}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </PageContainer>
  );
}

/* --- Relatorio Modal (rede-wide) ----------------------------------------- */
function RelatorioModal({ relatorio, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!relatorio) return null;

  const copyText = () => {
    const txt = [
      relatorio.resumo || "",
      "\nPontos Fortes:\n" + (relatorio.pontos_fortes || []).map(p => "- " + p).join("\n"),
      "\nPontos de Melhoria:\n" + (relatorio.pontos_melhoria || []).map(p => "- " + p).join("\n"),
      "\nRecomendações:\n" + (relatorio.recomendacoes_prioritarias || []).map(r => `- ${r.acao} (${r.prazo})`).join("\n"),
      relatorio.proximo_ciclo ? "\nPróximo Ciclo:\n" + relatorio.proximo_ciclo : "",
    ].join("\n");
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 800, maxHeight: "90vh",
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: 24,
        boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        <div style={{ background: T.gradientAccent, padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: T.radiusSm, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FileBarChart size={18} color="#fff"/>
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: T.fontTitle, letterSpacing: "-0.01em" }}>Relatório Executivo Geral</span>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={copyText} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: T.radiusSm,
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", fontSize: 13, cursor: "pointer",
              transition: T.transitionFast, fontFamily: T.fontBody, fontWeight: 600,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              {copied ? <Check size={14}/> : <Copy size={14}/>}
              {copied ? "Texto Copiado!" : "Copiar Texto"}
            </button>
            <button onClick={onClose} style={{
              width: 36, height: 36, borderRadius: T.radiusSm,
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: T.transitionFast,
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            >
              <X size={16}/>
            </button>
          </div>
        </div>

        <div style={{ padding: "32px", overflowY: "auto", flex: 1, color: T.textHi }}>
          {relatorio.resumo && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontFamily: T.fontBody }}>Resumo Estratégico</div>
              <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.7, margin: 0, fontFamily: T.fontBody }}>{relatorio.resumo}</p>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
            {(relatorio.pontos_fortes || []).length > 0 && (
              <div style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: T.radius, padding: "20px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.green, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, fontFamily: T.fontBody }}>Fortalezas da Rede</div>
                {relatorio.pontos_fortes.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 13, color: T.textMid, lineHeight: 1.6, fontWeight: 500, fontFamily: T.fontBody }}>
                    <span style={{ color: T.green, flexShrink: 0, fontWeight: 800 }}>+</span> {p}
                  </div>
                ))}
              </div>
            )}
            {(relatorio.pontos_melhoria || []).length > 0 && (
              <div style={{ background: "rgba(244,63,94,0.05)", border: "1px solid rgba(244,63,94,0.2)", borderRadius: T.radius, padding: "20px" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: T.red, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, fontFamily: T.fontBody }}>Pontos de Atenção Críticos</div>
                {relatorio.pontos_melhoria.map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 13, color: T.textMid, lineHeight: 1.6, fontWeight: 500, fontFamily: T.fontBody }}>
                    <span style={{ color: T.red, flexShrink: 0, fontWeight: 800 }}>!</span> {p}
                  </div>
                ))}
              </div>
            )}
          </div>

          {(relatorio.recomendacoes_prioritarias || []).length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, fontFamily: T.fontBody }}>Plano de Ação (Prioridades)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {relatorio.recomendacoes_prioritarias.map((r, i) => (
                  <div key={i} style={{ padding: T.tableCellPadding, borderRadius: 14, background: T.bgBase, border: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ fontSize: 14, color: T.textHi, fontWeight: 600, fontFamily: T.fontBody }}>{r.acao}</div>
                      {r.prazo && <Badge color={T.accent}>{r.prazo}</Badge>}
                    </div>
                    {r.impacto && <div style={{ fontSize: 13, color: T.textMid, marginTop: 6, fontWeight: 500, fontFamily: T.fontBody }}><strong>Impacto:</strong> {r.impacto}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {relatorio.proximo_ciclo && (
            <div style={{ padding: T.tableCellPadding, borderRadius: 14, background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: T.amber, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: T.fontBody }}>Diretriz de Próximo Ciclo</div>
              <p style={{ fontSize: 14, color: T.textMid, margin: 0, lineHeight: 1.6, fontWeight: 500, fontFamily: T.fontBody }}>{relatorio.proximo_ciclo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --- Main CRM Component -------------------------------------------------- */
export default function CRM() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [relatorio, setRelatorio]   = useState(null);
  const [gerandoRel, setGerandoRel] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);

  useEffect(() => {
    const s = Object.assign(document.createElement("style"), { textContent: PRINT_STYLE });
    document.head.appendChild(s);
    fetch("/api/crm/overview", { credentials: "include" })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
    return () => { try { document.head.removeChild(s); } catch {} };
  }, []);

  if (loading) return <PageLoading label="Carregando dados da rede corporativa..." />;
  if (!data || !data.hospitais) return (
    <PageContainer maxWidth={1400}>
      <div style={{ padding: 40, color: T.textLow, fontSize: 14, fontFamily: T.fontBody }}>Sem dados de rede disponíveis para exibição.</div>
    </PageContainer>
  );

  // If a hospital is selected, show drill-down detail
  if (selectedHospital) {
    return <HospitalDetail hospital={selectedHospital} onClose={() => setSelectedHospital(null)} />;
  }

  const { hospitais = [], totalHospitais = 0, mediaNPS = 0, melhor, pior, alertas = [] } = data;

  const filtered = search
    ? hospitais.filter(h => h.nome.toLowerCase().includes(search.toLowerCase()))
    : hospitais;

  return (
    <PageContainer maxWidth={1400}>
      <style>{PRINT_STYLE}</style>

      {/* Header */}
      <PageHeader icon={Network} title="Visão Corporativa" description="Gestão macro e acompanhamento de todas as unidades">
        <Button variant="primary" icon={FileBarChart} loading={gerandoRel} onClick={async () => {
          setGerandoRel(true);
          try {
            const r = await fetch("/api/crm/relatorio", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ unidade: data?.hospitais?.[0]?.nome || window.usuarioUnidade || "Minha Instituição" }),
            });
            const d = await r.json();
            if (d.relatorio) setRelatorio(d.relatorio);
          } catch {}
          setGerandoRel(false);
        }} className="no-print">
          {gerandoRel ? "Analisando..." : "Relatório Executivo da Rede"}
        </Button>
        <Button variant="secondary" icon={Download} onClick={() => exportCSV(filtered)} className="no-print">
          Exportar XML/CSV
        </Button>
      </PageHeader>

      {/* KPI Row */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <KpiBox label="Unidades Ativas" value={totalHospitais} icon={Building2} color={T.accent}/>
        <KpiBox label="Média NPS Corporativa" value={`${mediaNPS}`}
          sub={mediaNPS >= 50 ? "Rede em Excelência" : mediaNPS >= 0 ? "Rede Estável" : "Rede em Estado Crítico"}
          icon={mediaNPS >= 0 ? TrendingUp : TrendingDown}
          color={getNPSColor(mediaNPS)}/>
        {melhor && (
          <KpiBox label="Unidade Destaque"
            value={`NPS ${melhor.nps}`}
            sub={melhor.nome}
            icon={TrendingUp}
            color={T.green}/>
        )}
        {pior && (
          <KpiBox label="Unidade Prioritária"
            value={`NPS ${pior.nps}`}
            sub={pior.nome}
            icon={TrendingDown}
            color={pior.nps < 0 ? T.red : T.amber}/>
        )}
        {alertas.length > 0 && (
          <KpiBox label="Gestão de Crise"
            value={alertas.length}
            sub="unidades com NPS negativo"
            icon={AlertTriangle}
            color={T.red}/>
        )}
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card style={{ marginBottom: 32, borderColor: "rgba(244,63,94,0.3)", background: "rgba(244,63,94,0.03)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: T.iconBoxSizeSm, height: T.iconBoxSizeSm, borderRadius: T.iconBoxRadiusSm, background: "rgba(244,63,94,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AlertTriangle size={16} color={T.red}/>
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, fontFamily: T.fontTitle, color: T.red, letterSpacing: "-0.01em" }}>
              Atenção: Unidades com Performance Crítica
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {alertas.map(h => (
              <div key={h.nome} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "16px", borderRadius: T.radius,
                background: T.bgCard, border: "1px solid rgba(244,63,94,0.2)",
                cursor: "pointer", transition: T.transitionFast,
                boxShadow: "0 4px 12px rgba(244,63,94,0.05)"
              }}
                onClick={() => setSelectedHospital(h)}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(244,63,94,0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(244,63,94,0.05)"; }}
              >
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.red, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 14, color: T.textHi, fontWeight: 600, fontFamily: T.fontBody }}>{h.nome}</span>
                <span style={{ fontSize: 16, fontWeight: 800, fontFamily: T.fontTitle, color: T.red }}>NPS {h.nps}</span>
                <ChevronRight size={16} color={T.textLow}/>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Search & Relatorio Container */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div className="no-print" style={{ width: 320 }}>
            <Input
              icon={Search}
              placeholder="Buscar unidade de negócio..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
      </div>

      {/* Relatorio Modal */}
      {relatorio && <RelatorioModal relatorio={relatorio} onClose={() => setRelatorio(null)}/>}

      {/* Hospital Table */}
      <Card noPadding>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}`, background: T.bgBase }}>
              {["Código", "Unidade", "Performance NPS", "Volumetria", "Abrangência (Áreas)", "Classificação", ""].map(h => (
                <th key={h} style={{ padding: T.tableHeaderPadding, textAlign: "left", fontSize: T.tableHeaderFontSize, color: T.textLow, fontWeight: T.tableHeaderWeight, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.fontBody }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((h, i) => {
              const color = getNPSColor(h.nps);
              const badge = getNPSBadge(h.nps);
              const areaCount = Object.keys(h.areas || {}).length;
              return (
                <tr key={h.nome}
                  style={{ borderBottom: `1px solid ${T.border}`, transition: T.transitionFast, cursor: "pointer" }}
                  onClick={() => setSelectedHospital(h)}
                  onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: T.tableCellPadding, fontSize: 13, color: T.textLow, fontWeight: 600, width: 40, fontFamily: T.fontBody }}>{(i + 1).toString().padStart(3, '0')}</td>
                  <td style={{ padding: T.tableCellPadding }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: T.radiusSm,
                        background: `${color}15`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        <Building2 size={16} color={color}/>
                      </div>
                      <span style={{ fontSize: 15, color: T.textHi, fontWeight: 600, fontFamily: T.fontBody }}>{h.nome}</span>
                    </div>
                  </td>
                  <td style={{ padding: T.tableCellPadding }}>
                    <span style={{ fontSize: 20, fontWeight: 800, fontFamily: T.fontTitle, color, letterSpacing: "-0.02em" }}>
                      {h.nps >= 0 ? "+" : ""}{h.nps}
                    </span>
                  </td>
                  <td style={{ padding: T.tableCellPadding, fontSize: 13, color: T.textMid, fontWeight: 500, fontFamily: T.fontBody }}>
                    {h.respostas ? h.respostas.toLocaleString("pt-BR") : "—"} resp.
                  </td>
                  <td style={{ padding: T.tableCellPadding }}>
                     <span style={{ fontSize: 13, color: T.textMid, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, fontFamily: T.fontBody }}>
                        <Network size={14} color={T.textLow} />
                        {areaCount} setor{areaCount !== 1 ? "es" : ""}
                     </span>
                  </td>
                  <td style={{ padding: T.tableCellPadding }}>
                    <Badge color={badge.color} size="md">
                      {badge.label}
                    </Badge>
                  </td>
                  <td style={{ padding: T.tableCellPadding, width: 40, textAlign: "right" }}>
                    <div style={{ width: T.iconBoxSizeSm, height: T.iconBoxSizeSm, borderRadius: T.iconBoxRadiusSm, background: T.bgInput, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ChevronRight size={16} color={T.textLow}/>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: T.textLow, fontSize: 14, fontWeight: 500, fontFamily: T.fontBody }}>
                {search ? "Nenhuma unidade de negócio encontrada com esse termo." : "Sem dados corporativos disponíveis para visualização."}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </PageContainer>
  );
}
